import React, { useEffect, useState, useRef } from "react";
import { Formik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { Link, useHistory, useLocation } from "react-router-dom";
import queryString from "query-string";
import {
  Section,
  InputField,
  AgGridDataTable,
  TonnageDisclaimer,
  ProgressComponent,
} from "../../../shared";
import { Form, Button, Col, Alert } from "react-bootstrap";
import { formConfig } from "./FormConfig";
import urlParameterHelper from "../../../../utilities/urlParameters";

const schema = yup.object({
  type: yup.string().required("Tonnage By is required."),
  entity: yup.string(),
  entityType: yup.string().required("Select By is required."),
  epaId: yup.string().when("entityType", {
    is: (val) => val === "EpaId",
    then: yup.string().required("ID Number is required."),
    otherwise: yup.string(),
  }),
  city: yup.string().when("type", {
    is: (val) => val === "1" || val === "2",
    then: yup.string().when("entityType", {
      is: (val) => val === "City",
      then: yup.string().required("City is required"),
      otherwise: yup.string(),
    }),
  }),
  county: yup.string().when("type", {
    is: (val) => val === "1" || val === "2",
    then: yup.string().when("entityType", {
      is: (val) => val === "County",
      then: yup.string().required("County is required."),
      otherwise: yup.string(),
    }),
  }),
  cupa: yup.string().when("entityType", {
    is: (val) => val === "CUPA",
    then: yup.string().required("CUPA is required."),
    otherwise: yup.string(),
  }),
  year: yup.string().required("Year is required."),
  greaterThan: yup.number().positive("Must be a positive number."),
});

const TonnageReport = () => {
  const agGridRef = useRef();
  const formikRef = useRef();
  const history = useHistory();
  const location = useLocation();
  const params = queryString.parse(location.search);
  const searchOnInit = Object.keys(params).length > 0 ? true : false;
  const [rowData, setRowData] = useState([]);
  const [formParams, setFormParams] = useState([]);
  const [toggleInputs, setToggleInputs] = useState({
    year: true,
    type: true,
  });
  const [hasError, setHasError] = useState({ message: "", error: false });
  const [columnDefs, setColumnDefs] = useState([{ headerName: "", field: "" }]);
  const initialFormValues = {
    type: params && params.type ? params.type : "",
    epaId: params && params.epaId ? params.epaId : "",
    entityType: params && params.entityType ? params.entityType : "",
    entity: params && params.entity ? params.entity : "",
    city: params && params.city ? params.city : "",
    county: params && params.county ? params.county : "",
    cupa: params && params.cupa ? params.cupa : "",
    year: params && params.year ? params.year : "",
  };
  const pageTitle = "Tonnage Report";
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [progressPercentage2, setProgressPercentage2] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // progress bar has issues if this is written without useState
  const [value] = useState(Math.round(Math.random() * (44 - 20)) + 20);
  
  const [stop, setStop] = useState(false);
  const [source, setSource] = useState(axios.CancelToken.source());
  history.listen(() => {
    cancelCall();
  });
  const cancelCall = () => {
    source.cancel();
    setSource(axios.CancelToken.source());
  };

  // const onResetForm = (e, formikBag) => {
  //   setFormFields([formConfig[0]]);
  // };

  const onSubmit = async (data, formikBag) => {
    searchTonnage(data, formikBag);
  };

  useEffect(() => {
    if (searchOnInit) {
      setTimeout(() => formikRef.current.submitForm());
    }
  }, [searchOnInit]);

  const searchTonnage = async (formData, formikBag) => {
    setIsLoading(true);
    let value = 100;
    let obj = {};

    Object.keys(formData).forEach((key) => {
      if (formData[key] !== "") {
        obj[key] = formData[key];
      }
    });

    const qs = urlParameterHelper.stringify(obj);
    window.history.replaceState(null, 'Hazardous Waste Tracking System', `${location.pathname}?${qs}`);

    setRowData([]);
    // agGridRef.current.api.showLoadingOverlay();

    await axios
      .get(`/api/v1/hwts/search/tonnage/year/wastecode?${qs}`, {
        cancelToken: source.token,
        onDownloadProgress: (progressEvent) => {
          let percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );

          if (percentCompleted > 90) {
            setProgressPercentage2(value >= 100 ? value - 1 : value++);
            setStop(true);
          }
        },
      })
      .then((res) => {
        setStop(false);
        if (res.data.totalRecords === 0) {
          agGridRef.current.api.showNoRowsOverlay();
        } else {
          setAgGrid(res.data);
          agGridRef.current.api.hideOverlay();
        }

        agGridRef.current.api.sizeColumnsToFit();
        setHasError({ message: "", error: false });
        formikBag.setSubmitting(false);
      })
      .then(async () => {
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        if (axios.isCancel(err)) {
          console.log("Axios Request canceled", err.message);
        } else if (err.response) {
          if (err.response.status === 400) {
            let requiredFields = {};

            Object.keys(err.response.data).forEach((key, i) => {
              requiredFields = {
                ...requiredFields,
                [key]: err.response.data[key][0],
              };
            });
            formikBag.setErrors(requiredFields);
          } else if (err.response.status === 500) {
            setHasError({
              message: "An error has occured. Please try again.",
              error: true,
            });
          }
          setColumnDefs([{ headerName: "", field: "" }]);
          setRowData([]);
        }
        agGridRef.current && agGridRef.current.api.showNoRowsOverlay();
        formikBag.setSubmitting(false);
      });
    setFormParams(formData);
  };

  const dateComparator = (date1, date2) => {
    var date1Number = monthToComparableNumber(date1);
    var date2Number = monthToComparableNumber(date2);
    if (date1Number === null && date2Number === null) {
      return 0;
    }
    if (date1Number === null) {
      return -1;
    }
    if (date2Number === null) {
      return 1;
    }
    return date1Number - date2Number;
  };

  const monthToComparableNumber = (date) => {
    if (
      date === undefined ||
      date === null ||
      (date.length > 10 && date.length < 9)
    ) {
      return null;
    }
    var yearNumber = date.substring(6, 10);
    var dayNumber = date.substring(3, 5);
    var monthNumber = date.substring(0, 2);
    var result = yearNumber * 10000 + monthNumber * 100 + dayNumber;
    return result;
  };

  const setAgGrid = (data) => {
    let columnsArr = data.waste[0];
    let colDefArr = [];
    Object.keys(columnsArr).forEach((col) => {
      if (customGridCells) {
        var cellRend = customGridCells.filter(
          (object) => object.columnName === col
        );
        if (cellRend.length > 0) {
          colDefArr.push({
            headerName: col.replace(/_/g, " "),
            field: col,
            sortable: true,
            comparator: col.toLowerCase().includes("date")
              ? dateComparator
              : null,
            cellRenderer: cellRend[0].cellRenderer,
          });
        } else {
          colDefArr.push({
            headerName: col.replace(/_/g, " "),
            field: col,
            sortable: true,
            comparator: col.toLowerCase().includes("date")
              ? dateComparator
              : null,
          });
        }
      } else {
        colDefArr.push({
          headerName: col.replace(/_/g, " "),
          field: col,
          sortable: true,
          comparator: col.toLowerCase().includes("date")
            ? dateComparator
            : null,
        });
      }
    });

    setColumnDefs(colDefArr);
    setRowData(data.waste);
  };

  const onSelectChange = (e) => {
    switch (e.target.name) {
      case "type":
        formikRef.current.setFieldValue("entityType", "");
        switch (e.target.value) {
          // Entity Type
          case "1":
            setToggleInputs({
              type: true,
              entity: true,
              entityType: true,
              year: true,
              greaterThan: true,
            });
            break;
          // Method Code
          case "2":
            setToggleInputs({
              type: true,
              entityType: true,
              year: true,
              methCode: true,
            });
            break;
          // Waste Code
          case "3":
            setToggleInputs({
              type: true,
              entityType: true,
              year: true,
              wasteCodeType: true,
              greaterThan: true,
            });
            break;
          default:
            break;
        }
        break;
      case "entityType":
        switch (e.target.value) {
          case "City":
            setToggleInputs((prevState) => {
              return {
                ...prevState,
                city: true,
                county: null,
                cupa: null,
                epaId: null,
                entity:
                  formikRef && formikRef.current.values.type === "1"
                    ? toggleInputs.entity
                    : null,
              };
            });
            formikRef.current.setFieldValue("county", "");
            formikRef.current.setFieldValue("cupa", "");
            formikRef.current.setFieldValue("epaId", "");
            formikRef.current.setFieldValue("entity", "");
            break;
          case "County":
            setToggleInputs((prevState) => {
              return {
                ...prevState,
                county: true,
                city: null,
                cupa: null,
                epaId: null,
                entity:
                  formikRef && formikRef.current.values.type === "1"
                    ? toggleInputs.entity
                    : null,
              };
            });
            formikRef.current.setFieldValue("city", "");
            formikRef.current.setFieldValue("cupa", "");
            formikRef.current.setFieldValue("epaId", "");
            formikRef.current.setFieldValue("entity", "");
            break;
          case "CUPA":
            setToggleInputs((prevState) => {
              return {
                ...prevState,
                cupa: true,
                city: null,
                county: null,
                epaId: null,
                entity:
                  formikRef && formikRef.current.values.type === "1"
                    ? toggleInputs.entity
                    : null,
              };
            });
            formikRef.current.setFieldValue("city", "");
            formikRef.current.setFieldValue("county", "");
            formikRef.current.setFieldValue("epaId", "");
            formikRef.current.setFieldValue("entity", "");
            break;
          case "EpaId":
            setToggleInputs((prevState) => {
              return {
                ...prevState,
                epaId: true,
                entity: true,
                city: null,
                county: null,
                cupa: null,
              };
            });
            formikRef.current.setFieldValue("city", "");
            formikRef.current.setFieldValue("county", "");
            formikRef.current.setFieldValue("cupa", "");
            break;
          default:
            break;
        }
        break;
      case "wasteCodeType":
        if (e.target.value === "rcra") {
          setToggleInputs((prevState) => {
            return {
              ...prevState,
              rcraWasteCode: true,
              caWasteCode: null,
              allWasteCode: null,
            };
          });
          break;
        } else if (e.target.value === "ca") {
          setToggleInputs((prevState) => {
            return {
              ...prevState,
              caWasteCode: true,
              rcraWasteCode: null,
              allWasteCode: null,
            };
          });
          break;
        } else if (e.target.value === "all") {
          setToggleInputs((prevState) => {
            return {
              ...prevState,
              allWasteCode: true,
              caWasteCode: null,
              rcraWasteCode: null,
            };
          });
          break;
        }
        break;
      default:
        break;
    }
  };

  const customGridCells = [
    {
      columnName: "Method_Code",
      cellRenderer: function (params) {
        if (params.value != null && formikRef.current.values.epaId !== "") {
          //    if (params.value != null) {
          return (
            `<a href="/reports/manifest?epaId=${params.data.ID_Number == null
              ? formikRef.current.values.epaId
              : params.data.ID_Number
            }&shipYear=${formikRef.current.values.year}&entityType=${formikRef.current.values.entity
            }&methCode=${params.data.Method_Code}&county=${formikRef.current.values.county
            }&city=${formikRef.current.values.city}&cupa=${formikRef.current.values.cupa
            }" target="_">` +
            params.value +
            "</a>"
          );
        } else {
          return (
            `<a href="/reports/manifest?epaId=${"UNKNOWN"}&shipYear=${formikRef.current.values.year
            }&entityType=${formikRef.current.values.entity}&methCode=${params.data.Method_Code
            }&county=${formikRef.current.values.county}&city=${formikRef.current.values.city
            }&cupa=${formikRef.current.values.cupa}" target="_">` +
            params.value +
            "</a>"
          );
        }
      },
    },
    {
      columnName: "Waste_Code",
      cellRenderer: function (params) {
        if (params.value != null && formikRef.current.values.epaId !== "") {
          //   if (params.value != null) {
          return (
            `<a href="/reports/manifest?epaId=${params.data.ID_Number == null
              ? formikRef.current.values.epaId
              : params.data.ID_Number
            }&shipYear=${formikRef.current.values.year}&entityType=${formikRef.current.values.entity
            }&wasteCd=${params.data.Waste_Code}&county=${formikRef.current.values.county
            }&city=${formikRef.current.values.city}&cupa=${formikRef.current.values.cupa
            }" target="_">` +
            params.value +
            "</a>"
          );
        } else {
          return (
            `<a href="/reports/manifest?epaId=${"UNKNOWN"}&shipYear=${formikRef.current.values.year
            }&entityType=${formikRef.current.values.entity}&wasteCd=${params.data.Waste_Code
            }&county=${formikRef.current.values.county}&city=${formikRef.current.values.city
            }&cupa=${formikRef.current.values.cupa}" target="_">` +
            params.value +
            "</a>"
          );
        }
      },
    },
    {
      columnName: "ID_Number",
      cellRenderer: function (params) {
        if (params.value != null) {
          return (
            `<a href="/facility/${params.value}" target="_">` +
            params.value +
            "</a>"
          );
        }
      },
    },
    {
      columnName: "Quantity_In_Tons",
      cellRenderer: function (params) {
        if (
          params.value != null &&
          (params.data.ID_Number != null ||
            formikRef.current.values.epaId !== "")
        ) {
          return (
            `<a href="/reports/hazardous-waste-summary?epaId=${params.data.ID_Number == null
              ? formikRef.current.values.epaId
              : params.data.ID_Number
            }&handlerType=${formikRef.current.values.entity ===
              ("DesignatedFacility" || "AlternateDesignateFacility")
              ? "TSDF"
              : formikRef.current.values.entity
            }&shipYear=${formikRef.current.values.year}" target="_">` +
            params.value +
            "</a>"
          );
        } else {
          return params.value;
        }
      },
    },
    {
      columnName: "Manifest_Count",
      cellRenderer: function (params) {
        if (params.value != null) {
          return (
            `<a href="/reports/manifest?epaId=${params.data.ID_Number == null
              ? formikRef.current.values.epaId
              : params.data.ID_Number
            }&shipYear=${formikRef.current.values.year}&entityType=${formikRef.current.values.entity
            }" target="_">` +
            params.value +
            "</a>"
          );
        }
      },
    },
  ];

  return (
    <div>
      <Section title="Tonnage Report" type={"standout"} />
      <div id="page-container" className="container" style={{ position: "relative" }}>
        <ol className="breadcrumb">
          <li>
            <Link to="/">Home</Link>
          </li>
          <li>
            <Link to="/reports" title="Home page">
              HWTS Reports
            </Link>
          </li>
          <li className="active">Tonnage Report</li>
        </ol>

        <Formik
          innerRef={formikRef}
          validateOnChange={false}
          validateOnBlur={false}
          validationSchema={schema}
          onSubmit={(data, { setSubmitting, resetForm, setErrors }) => {
            setSubmitting(true);
            onSubmit(data, { setSubmitting, resetForm, setErrors });
          }}
          initialValues={initialFormValues}
        >
          {({
            isSubmitting,
            handleReset,
            handleSubmit,
            handleChange,
            values,
            touched,
            errors,
            resetForm,
            setFieldValue,
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Form.Row>
                {formConfig.map(
                  (input, i) =>
                    toggleInputs[input.name] && (
                      <InputField
                        key={i}
                        initialFormValues={initialFormValues}
                        name={input.name}
                        config={input.config}
                        values={values}
                        handleChange={(e) => {
                          handleChange(e);
                          onSelectChange(e);
                        }}
                        errors={errors}
                        touched={touched}
                        validationSchema={schema}
                        setFieldValue={setFieldValue}
                      />
                    )
                )}
              </Form.Row>
              <Form.Row>
                <Form.Group as={Col} md="1">
                  {isSubmitting === true ? (
                    <div
                      className="cancel-button btn btn-block-xs"
                      style={{ minWidth: "75px", maxHeight: "39px" }}
                      onClick={() => {
                        cancelCall();
                      }}
                      title="Cancel Search"
                    >
                      <span className="ca-gov-icon-do-not-sign formearch">
                        <span className="sr-only">Cancel</span>
                      </span>
                    </div>
                  ) : (
                    <Button type="submit" disabled={isSubmitting === true}>
                      Search
                    </Button>
                  )}
                </Form.Group>
                <Form.Group as={Col} md="1">
                  <Button
                    onClick={resetForm}
                    type="reset"
                    disabled={isSubmitting}
                  >
                    Reset
                  </Button>
                </Form.Group>
              </Form.Row>
              {hasError.error && (
                <Alert variant="danger">{hasError.message}</Alert>
              )}
            </Form>
          )}
        </Formik>
        <div
          className="mb-2"
          style={{ fontSize: "1rem", fontStyle: "italic", textAlign: "center" }}
        >
          Disclaimer: The sum of the quantities may exceed the total tonnage
          because a manifest waste stream may contain multiple waste codes.
        </div>
        <div style={{ position: "relative" }}>
          {rowData.length > 0 ? (
            <span
              className="m-r-md"
              style={{
                position: "relative",
                bottom: "-30px",
                fontWeight: "bold",
              }}
            >
              {/* {rowData.length} Records */}
            </span>
          ) : null}

        </div>

        <ProgressComponent
          percent={progressPercentage}
          setProgressPercentage={setProgressPercentage}
          percent2={progressPercentage2}
          grid={true}
          value={value}
          isLoading={isLoading}
          stop={stop}
        />
        <AgGridDataTable
          formParams={formParams}
          formConfig={formConfig}
          pageTitle={pageTitle}
          agGridRef={agGridRef}
          columnDefs={columnDefs}
          rowData={rowData}
          initialPageSize={50}
          formRef={formikRef}
        />
        <div className="m-b-md"></div>
        <TonnageDisclaimer />
      </div>
    </div>
  );
};

// const TonnageReportAnt = Form.create()(TonnageReport);

export default TonnageReport;
